import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  IconWorld, IconPalette, IconLayoutList, IconPhoto, IconBed,
  IconToolsKitchen2, IconSparkles, IconSearch, IconMail, IconCheck,
  IconX, IconPlus, IconTrash, IconRefresh, IconExternalLink, IconUpload,
  IconBrandGoogleDrive, IconEye, IconDeviceFloppy, IconAlertTriangle,
  IconChartBar, IconArrowRight, IconShare, IconArrowsSort, IconPlayerPlay,
  IconVideo, Icon360View, IconBox
} from "@tabler/icons-react";
import * as wbApi from "../controllers/website_builder.controller";
import { useStoreSettings } from "../controllers/settings.controller";

export default function WebsiteBuilderPage() {
  const { data: storeSettings } = useStoreSettings();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Data states
  const [settings, setSettings] = useState(null);
  const [sections, setSections] = useState([]);
  const [mediaList, setMediaList] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [contentList, setContentList] = useState([]);
  const [seoAudit, setSeoAudit] = useState(null);
  const [redirects, setRedirects] = useState([]);
  const [enquiries, setEnquiries] = useState([]);

  // Modals & form helpers
  const [driveModalOpen, setDriveModalOpen] = useState(false);
  const [driveLinksText, setDriveLinksText] = useState("");
  const [driveMediaType, setDriveMediaType] = useState("image");
  const [driveTags, setDriveTags] = useState("");

  const [editMediaModal, setEditMediaModal] = useState(null);
  const [editContentModal, setEditContentModal] = useState(null);
  const [newRedirectModal, setNewRedirectModal] = useState(false);
  const [redirectForm, setRedirectForm] = useState({ from_path: "", to_path: "", type: 301 });

  // Initial load
  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === "sections") loadSections();
    if (activeTab === "media") loadMedia();
    if (activeTab === "rooms") loadRooms();
    if (activeTab === "content") loadContent();
    if (activeTab === "seo") loadSeo();
    if (activeTab === "enquiries") loadEnquiries();
  }, [activeTab]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await wbApi.getWebsiteSettings();
      if (res.success) {
        setSettings(res.settings);
      }
    } catch (err) {
      toast.error("Failed to load website settings");
    } finally {
      setLoading(false);
    }
  };

  const loadSections = async () => {
    try {
      const res = await wbApi.getWebsiteSections();
      if (res.success) setSections(res.sections);
    } catch (_) {}
  };

  const loadMedia = async () => {
    try {
      const res = await wbApi.getWebsiteMedia();
      if (res.success) setMediaList(res.media);
    } catch (_) {}
  };

  const loadRooms = async () => {
    try {
      const res = await wbApi.getWebsiteRooms();
      if (res.success) setRooms(res.rooms);
    } catch (_) {}
  };

  const loadContent = async () => {
    try {
      const res = await wbApi.getWebsiteContent();
      if (res.success) setContentList(res.items);
    } catch (_) {}
  };

  const loadSeo = async () => {
    try {
      const [auditRes, redRes] = await Promise.all([
        wbApi.getWebsiteSeoAudit(),
        wbApi.getWebsiteRedirects(),
      ]);
      if (auditRes.success) setSeoAudit(auditRes.audit);
      if (redRes.success) setRedirects(redRes.redirects);
    } catch (_) {}
  };

  const loadEnquiries = async () => {
    try {
      const res = await wbApi.getWebsiteEnquiries();
      if (res.success) setEnquiries(res.enquiries);
    } catch (_) {}
  };

  // Actions
  const handleSaveSettings = async (customData = null) => {
    setSaving(true);
    try {
      const payload = customData || settings;
      const res = await wbApi.updateWebsiteSettings(payload);
      if (res.success) {
        toast.success(res.message || "Settings saved!");
        loadSettings();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      const res = await wbApi.publishWebsite();
      if (res.success) {
        toast.success(res.message || "Website published and cache cleared!");
      }
    } catch (_) {
      toast.error("Failed to publish website");
    } finally {
      setSaving(false);
    }
  };

  const handleAddDriveMedia = async () => {
    if (!driveLinksText.trim()) {
      toast.error("Please enter at least one Google Drive link");
      return;
    }
    const links = driveLinksText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      const res = await wbApi.addDriveMedia({
        links,
        media_type: driveMediaType,
        tags: driveTags,
      });
      if (res.success) {
        toast.success(res.message);
        setDriveModalOpen(false);
        setDriveLinksText("");
        loadMedia();
      }
    } catch (err) {
      toast.error("Failed to add Drive media");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("title", file.name);

    setSaving(true);
    try {
      const res = await wbApi.uploadWebsiteMedia(formData);
      if (res.success) {
        toast.success(res.message);
        loadMedia();
      }
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMedia = async (id) => {
    if (!window.confirm("Are you sure you want to delete this media item?")) return;
    try {
      const res = await wbApi.deleteWebsiteMedia(id);
      if (res.success) {
        toast.success("Media deleted");
        loadMedia();
      }
    } catch (_) {
      toast.error("Failed to delete media");
    }
  };

  const handleUpdateMedia = async (e) => {
    e.preventDefault();
    if (!editMediaModal) return;
    try {
      const res = await wbApi.updateWebsiteMedia(editMediaModal.id, editMediaModal);
      if (res.success) {
        toast.success("Media updated");
        setEditMediaModal(null);
        loadMedia();
      }
    } catch (_) {
      toast.error("Failed to update media");
    }
  };

  const handleSaveRedirect = async (e) => {
    e.preventDefault();
    try {
      const res = await wbApi.saveWebsiteRedirect(redirectForm);
      if (res.success) {
        toast.success(res.message);
        setNewRedirectModal(false);
        setRedirectForm({ from_path: "", to_path: "", type: 301 });
        loadSeo();
      }
    } catch (_) {
      toast.error("Failed to save redirect");
    }
  };

  const handleDeleteRedirect = async (id) => {
    try {
      const res = await wbApi.deleteWebsiteRedirect(id);
      if (res.success) {
        toast.success(res.message);
        loadSeo();
      }
    } catch (_) {
      toast.error("Failed to delete redirect");
    }
  };

  const handleUpdateRoomMeta = async (roomTypeId, data) => {
    try {
      const res = await wbApi.updateWebsiteRoom(roomTypeId, data);
      if (res.success) {
        toast.success(res.message);
        loadRooms();
      }
    } catch (_) {
      toast.error("Failed to update room website data");
    }
  };

  // Preset Theme selector
  const applyPresetTheme = (preset) => {
    const presets = {
      "royal-gold": {
        preset: "royal-gold",
        mode: "dark",
        colors: {
          primary: "#C8A25C",
          secondary: "#0F172A",
          accent: "#E9D8A6",
          background: "#0B0B0F",
          surface: "#15151C",
          text: "#F5F1E8",
          muted: "#A39E93",
        },
        fonts: { heading: "Playfair Display", body: "Inter" },
        intro: { enabled: true, style: "doors", text: "Step Inside" },
      },
      "himalayan-minimal": {
        preset: "himalayan-minimal",
        mode: "light",
        colors: {
          primary: "#1E3A8A",
          secondary: "#F8FAFC",
          accent: "#38BDF8",
          background: "#FFFFFF",
          surface: "#F1F5F9",
          text: "#0F172A",
          muted: "#64748B",
        },
        fonts: { heading: "Cinzel", body: "Plus Jakarta Sans" },
        intro: { enabled: true, style: "fade", text: "Welcome" },
      },
      "midnight-luxe": {
        preset: "midnight-luxe",
        mode: "dark",
        colors: {
          primary: "#EC4899",
          secondary: "#18181B",
          accent: "#F472B6",
          background: "#09090B",
          surface: "#18181B",
          text: "#FAFAFA",
          muted: "#71717A",
        },
        fonts: { heading: "Syne", body: "Inter" },
        intro: { enabled: true, style: "doors", text: "Experience Luxury" },
      },
      "boutique-earth": {
        preset: "boutique-earth",
        mode: "light",
        colors: {
          primary: "#2D6A4F",
          secondary: "#F3E9DC",
          accent: "#D8F3DC",
          background: "#FAF7F2",
          surface: "#E9ECEF",
          text: "#1B4332",
          muted: "#52796F",
        },
        fonts: { heading: "Cormorant Garamond", body: "Mulish" },
        intro: { enabled: true, style: "doors", text: "Enter Sanctuary" },
      },
    };

    if (presets[preset]) {
      setSettings((prev) => ({
        ...prev,
        theme: {
          ...prev.theme,
          ...presets[preset],
        },
      }));
      toast.success(`Applied ${preset} theme palette`);
    }
  };

  const previewUrl = settings?.custom_domain
    ? `https://${settings.custom_domain}`
    : `https://ehospitalitynepal.com/site/${settings?.site_slug || "preview"}`;

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-6 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded-xl">
              <IconWorld size={28} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Website Builder & SEO Engine
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Manage your hotel & restaurant website, media from Google Drive, and SEO ranking from your ERP
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 rounded-xl transition"
          >
            <IconExternalLink size={18} />
            View Live Site
          </a>
          <button
            onClick={handlePublish}
            disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-sm transition disabled:opacity-50"
          >
            <IconDeviceFloppy size={18} />
            {saving ? "Publishing..." : "Publish & Purge Cache"}
          </button>
        </div>
      </div>

      {/* Tabs Bar */}
      <div className="flex overflow-x-auto no-scrollbar gap-2 border-b border-slate-200 dark:border-slate-700 pb-2">
        {[
          { id: "overview", label: "Overview & Domain", icon: IconWorld },
          { id: "theme", label: "Theme & Design", icon: IconPalette },
          { id: "sections", label: "Homepage Sections", icon: IconLayoutList },
          { id: "media", label: "Media Library (Drive & Uploads)", icon: IconPhoto },
          { id: "rooms", label: "Rooms & 360° Tours", icon: IconBed },
          { id: "dining", label: "Dining & Menu", icon: IconToolsKitchen2 },
          { id: "content", label: "Experiences & Offers", icon: IconSparkles },
          { id: "seo", label: "SEO & Audit", icon: IconSearch },
          { id: "enquiries", label: `Enquiries (${enquiries.length})`, icon: IconMail },
        ].map((t) => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl whitespace-nowrap transition ${
                active
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <Icon size={18} />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && !settings ? (
        <div className="py-20 text-center text-slate-400">Loading website platform...</div>
      ) : null}

      {/* TAB 1: OVERVIEW & DOMAIN */}
      {activeTab === "overview" && settings && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-5">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Basic Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                    Website Name *
                  </label>
                  <input
                    type="text"
                    value={settings.site_name || ""}
                    onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                    Website URL Slug *
                  </label>
                  <div className="flex items-center">
                    <span className="px-3 py-2.5 bg-slate-100 dark:bg-slate-700 text-slate-500 border border-r-0 border-slate-200 dark:border-slate-700 rounded-l-xl text-xs">
                      ehospitalitynepal.com/site/
                    </span>
                    <input
                      type="text"
                      value={settings.site_slug || ""}
                      onChange={(e) => setSettings({ ...settings, site_slug: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-r-xl text-sm"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                    Tagline / Hero Headline
                  </label>
                  <input
                    type="text"
                    value={settings.tagline || ""}
                    onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                    placeholder="Where every stay becomes an unforgettable story"
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                    Custom Domain (e.g. www.grandhimalayahotel.com)
                  </label>
                  <input
                    type="text"
                    value={settings.custom_domain || ""}
                    onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })}
                    placeholder="www.yourhotel.com"
                    className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Point your domain CNAME or A Record to <code className="text-indigo-500 font-mono">202.51.83.60</code> to connect your custom domain.
                  </p>
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
                <button
                  onClick={() => handleSaveSettings()}
                  disabled={saving}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl text-sm transition"
                >
                  Save Basic Info
                </button>
              </div>
            </div>

            {/* Modules Toggles */}
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Active Website Modules</h2>
              <p className="text-xs text-slate-500">
                Enable or disable sections on your website dynamically based on your property type
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(settings.modules || {}).map(([key, enabled]) => (
                  <label
                    key={key}
                    className={`flex items-center justify-between p-3.5 rounded-xl border cursor-pointer transition ${
                      enabled
                        ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20 text-indigo-900 dark:text-indigo-200"
                        : "border-slate-200 dark:border-slate-700 text-slate-500"
                    }`}
                  >
                    <span className="capitalize text-sm font-medium">{key.replace("_", " ")}</span>
                    <input
                      type="checkbox"
                      checked={!!enabled}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          modules: { ...settings.modules, [key]: e.target.checked },
                        })
                      }
                      className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                  </label>
                ))}
              </div>
              <div className="pt-2 flex justify-end">
                <button
                  onClick={() => handleSaveSettings()}
                  className="px-5 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium rounded-xl text-sm"
                >
                  Save Modules
                </button>
              </div>
            </div>
          </div>

          {/* Right Sidebar Status */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
              <h3 className="font-bold text-slate-900 dark:text-white">Website Status</h3>
              <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-900 rounded-xl">
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Online Visibility
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    settings.is_published
                      ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                  }`}
                >
                  {settings.is_published ? "PUBLISHED" : "DRAFT"}
                </span>
              </div>
              <button
                onClick={() =>
                  handleSaveSettings({ ...settings, is_published: !settings.is_published })
                }
                className={`w-full py-2.5 rounded-xl text-sm font-semibold transition ${
                  settings.is_published
                    ? "bg-red-50 text-red-600 hover:bg-red-100"
                    : "bg-green-600 text-white hover:bg-green-700"
                }`}
              >
                {settings.is_published ? "Unpublish Website" : "Publish Live Website"}
              </button>

              <div className="text-xs text-slate-500 space-y-1 pt-2">
                <div>Tenant ID: <span className="font-mono">{settings.tenant_id}</span></div>
                <div>Currency: <span className="font-semibold">{settings.currency}</span></div>
                <div>Business Type: <span className="capitalize">{settings.business_type}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: THEME & DESIGN */}
      {activeTab === "theme" && settings && (
        <div className="space-y-6">
          {/* Preset Cards */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Luxury Theme Presets</h2>
            <p className="text-xs text-slate-500">
              One-click aesthetic designs tailored for 5-star hotels, luxury resorts, boutique stays, and fine dining
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { id: "royal-gold", name: "Royal Gold", bg: "#0B0B0F", p: "#C8A25C", desc: "Gold & Dark Obsidian luxury hotel look" },
                { id: "himalayan-minimal", name: "Himalayan Minimal", bg: "#FFFFFF", p: "#1E3A8A", desc: "Crisp white, deep navy & sky blue" },
                { id: "midnight-luxe", name: "Midnight Luxe", bg: "#09090B", p: "#EC4899", desc: "Vibrant neon rose & sleek dark obsidian" },
                { id: "boutique-earth", name: "Boutique Earth", bg: "#FAF7F2", p: "#2D6A4F", desc: "Organic warm beige & forest green retreat" },
              ].map((th) => (
                <div
                  key={th.id}
                  onClick={() => applyPresetTheme(th.id)}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition hover:scale-102 ${
                    settings.theme?.preset === th.id
                      ? "border-indigo-600 ring-2 ring-indigo-200"
                      : "border-slate-200 dark:border-slate-700"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-5 h-5 rounded-full" style={{ backgroundColor: th.bg, border: "1px solid #ccc" }} />
                    <span className="w-5 h-5 rounded-full" style={{ backgroundColor: th.p }} />
                    <span className="font-bold text-sm text-slate-900 dark:text-white ml-1">{th.name}</span>
                  </div>
                  <p className="text-xs text-slate-500">{th.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Color Palettes Customizer */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-5">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Custom Color Palette</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(settings.theme?.colors || {}).map(([cKey, colorVal]) => (
                <div key={cKey}>
                  <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">{cKey}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={colorVal}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          theme: {
                            ...settings.theme,
                            colors: { ...settings.theme.colors, [cKey]: e.target.value },
                          },
                        })
                      }
                      className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0"
                    />
                    <input
                      type="text"
                      value={colorVal}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          theme: {
                            ...settings.theme,
                            colors: { ...settings.theme.colors, [cKey]: e.target.value },
                          },
                        })
                      }
                      className="w-full px-3 py-2 text-xs font-mono bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-slate-100 dark:border-slate-700 flex justify-end">
              <button
                onClick={() => handleSaveSettings()}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl text-sm"
              >
                Save Theme
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: HOMEPAGE SECTIONS */}
      {activeTab === "sections" && (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Homepage Sections</h2>
              <p className="text-xs text-slate-500">Toggle and arrange the storytelling flow of your website</p>
            </div>
            <button
              onClick={() => wbApi.saveWebsiteSections(sections).then(() => toast.success("Sections saved!"))}
              className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-xl text-sm"
            >
              Save Sections Flow
            </button>
          </div>

          <div className="space-y-3">
            {sections.map((sec, idx) => (
              <div
                key={sec.id || idx}
                className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700"
              >
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300 rounded-lg flex items-center justify-center font-bold text-xs">
                    {idx + 1}
                  </span>
                  <div>
                    <h4 className="font-bold text-sm text-slate-900 dark:text-white capitalize">
                      {sec.section_type.replace("_", " ")}
                    </h4>
                    <p className="text-xs text-slate-400">{sec.content?.title || "Default section content"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold">
                    <span>{sec.is_enabled ? "Enabled" : "Disabled"}</span>
                    <input
                      type="checkbox"
                      checked={!!sec.is_enabled}
                      onChange={(e) => {
                        const copy = [...sections];
                        copy[idx].is_enabled = e.target.checked;
                        setSections(copy);
                      }}
                      className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 4: MEDIA LIBRARY (GOOGLE DRIVE & UPLOADS) */}
      {activeTab === "media" && (
        <div className="space-y-6">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">Media Library</h2>
              <p className="text-xs text-slate-500">
                High-resolution images, Google Drive videos with range seeking, 360° panoramas & 3D models
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setDriveModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl text-sm transition"
              >
                <IconBrandGoogleDrive size={18} />
                Add Google Drive Links
              </button>
              <label className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-white font-medium rounded-xl text-sm cursor-pointer transition">
                <IconUpload size={18} />
                Upload File
                <input type="file" onChange={handleFileUpload} className="hidden" />
              </label>
            </div>
          </div>

          {/* Media Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {mediaList.map((m) => (
              <div
                key={m.id}
                className="group relative bg-white dark:bg-slate-800 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col"
              >
                <div className="relative aspect-video bg-slate-100 dark:bg-slate-900 flex items-center justify-center overflow-hidden">
                  {m.type === "video" ? (
                    <div className="flex flex-col items-center text-slate-400">
                      <IconVideo size={32} />
                      <span className="text-[10px] mt-1 uppercase font-semibold">Video</span>
                    </div>
                  ) : m.type === "panorama360" ? (
                    <div className="flex flex-col items-center text-slate-400">
                      <Icon360View size={32} />
                      <span className="text-[10px] mt-1 uppercase font-semibold">360° View</span>
                    </div>
                  ) : m.type === "model3d" ? (
                    <div className="flex flex-col items-center text-slate-400">
                      <IconBox size={32} />
                      <span className="text-[10px] mt-1 uppercase font-semibold">3D Model</span>
                    </div>
                  ) : (
                    <img
                      src={m.url}
                      alt={m.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=500";
                      }}
                    />
                  )}
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-black/60 text-white">
                    {m.source}
                  </span>
                </div>
                <div className="p-3 flex-1 flex flex-col justify-between">
                  <div>
                    <h5 className="font-bold text-xs truncate text-slate-900 dark:text-white" title={m.title}>
                      {m.title || "Untitled Media"}
                    </h5>
                    <p className="text-[10px] text-slate-400 truncate mt-0.5" title={m.alt}>
                      ALT: {m.alt || "None"}
                    </p>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100 dark:border-slate-700">
                    <button
                      onClick={() => setEditMediaModal(m)}
                      className="text-xs text-indigo-600 hover:underline font-semibold"
                    >
                      Edit SEO
                    </button>
                    <button
                      onClick={() => handleDeleteMedia(m.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <IconTrash size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {mediaList.length === 0 && (
            <div className="p-12 text-center text-slate-400 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
              No media added yet. Paste your Google Drive video or image links above!
            </div>
          )}
        </div>
      )}

      {/* TAB 5: ROOMS & 360° TOURS */}
      {activeTab === "rooms" && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">Online Room Showcases</h2>
            <p className="text-xs text-slate-500 mb-4">
              Add rich marketing descriptions, bed types, 360° tours, and videos to each ERP room type
            </p>

            <div className="space-y-4">
              {rooms.map((rm) => (
                <div
                  key={rm.id}
                  className="p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-bold text-base text-slate-900 dark:text-white">{rm.name}</h3>
                      <p className="text-xs text-slate-500">
                        ERP Rate: Rs. {rm.rate.toLocaleString()} / night • Total Rooms: {rm.total_rooms}
                      </p>
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                      <span>Visible Online</span>
                      <input
                        type="checkbox"
                        checked={rm.is_visible}
                        onChange={(e) => handleUpdateRoomMeta(rm.id, { is_visible: e.target.checked })}
                        className="rounded text-indigo-600 h-4 w-4"
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">
                        Short Highlight
                      </label>
                      <input
                        type="text"
                        defaultValue={rm.short_description || ""}
                        onBlur={(e) => handleUpdateRoomMeta(rm.id, { short_description: e.target.value })}
                        placeholder="Balcony view with king bed"
                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">
                        Bed & Sleep Type
                      </label>
                      <input
                        type="text"
                        defaultValue={rm.bed_type || ""}
                        onBlur={(e) => handleUpdateRoomMeta(rm.id, { bed_type: e.target.value })}
                        placeholder="1 King Bed or 2 Twin Beds"
                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold uppercase text-slate-500 mb-1">
                        Max Guests
                      </label>
                      <input
                        type="number"
                        defaultValue={rm.max_guests || 2}
                        onBlur={(e) => handleUpdateRoomMeta(rm.id, { max_guests: parseInt(e.target.value, 10) })}
                        className="w-full px-3 py-2 text-xs bg-white dark:bg-slate-800 border rounded-lg"
                      />
                    </div>
                  </div>

                  {/* Room Media Badges */}
                  <div className="flex items-center gap-3 text-xs text-slate-500 pt-2 border-t border-slate-200 dark:border-slate-800">
                    <span>Photos: <strong>{rm.gallery?.length || 0}</strong></span>
                    <span>360° Tours: <strong>{rm.tours?.length || 0}</strong></span>
                    <span>Videos: <strong>{rm.videos?.length || 0}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TAB 8: SEO & AUDIT ENGINE */}
      {activeTab === "seo" && (
        <div className="space-y-6">
          {/* SEO Score Banner */}
          {seoAudit && (
            <div className="bg-gradient-to-br from-indigo-900 to-slate-900 text-white p-6 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold uppercase tracking-wider">
                  Live SEO Audit
                </span>
                <h3 className="text-2xl font-bold mt-2">
                  Health Score: {seoAudit.overall_score}/100
                </h3>
                <p className="text-xs text-indigo-200 mt-1">
                  Scanned {seoAudit.page_count} public pages for keyword density, title lengths, meta descriptions & OG tags
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-full border-4 border-indigo-400 flex items-center justify-center text-3xl font-black">
                  {seoAudit.overall_score}%
                </div>
              </div>
            </div>
          )}

          {/* Audit Pages Checklist */}
          {seoAudit?.pages && (
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
              <h3 className="font-bold text-slate-900 dark:text-white">Page-by-Page Audit Findings</h3>
              <div className="space-y-3">
                {seoAudit.pages.map((p, idx) => (
                  <div key={idx} className="p-4 bg-slate-50 dark:bg-slate-900 rounded-xl border space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs font-bold text-indigo-600 dark:text-indigo-400">
                        {p.path}
                      </span>
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-800">
                        Score: {p.score}/100
                      </span>
                    </div>
                    <div className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                      Title: {p.title}
                    </div>
                    {p.issues?.length > 0 ? (
                      <div className="space-y-1 pt-1">
                        {p.issues.map((iss, iIdx) => (
                          <div
                            key={iIdx}
                            className={`text-[11px] flex items-center gap-1.5 ${
                              iss.level === "error" ? "text-red-600" : "text-amber-600"
                            }`}
                          >
                            <IconAlertTriangle size={14} />
                            {iss.msg}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-[11px] text-green-600 flex items-center gap-1">
                        <IconCheck size={14} /> All SEO tags optimal
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 301 Redirects Manager */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white">301 / 302 URL Redirects</h3>
                <p className="text-xs text-slate-500">
                  Prevent 404 broken links when moving pages or migrating from an older website
                </p>
              </div>
              <button
                onClick={() => setNewRedirectModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-semibold"
              >
                <IconPlus size={16} /> Add Redirect
              </button>
            </div>

            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {redirects.map((r) => (
                <div key={r.id} className="py-2.5 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-3">
                    <span className="font-mono bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded font-bold">
                      {r.type}
                    </span>
                    <span className="font-mono text-slate-500">{r.from_path}</span>
                    <IconArrowRight size={14} className="text-slate-400" />
                    <span className="font-mono font-bold text-slate-900 dark:text-white">{r.to_path}</span>
                    <span className="text-slate-400">({r.hits} hits)</span>
                  </div>
                  <button onClick={() => handleDeleteRedirect(r.id)} className="text-red-500 hover:text-red-700">
                    <IconTrash size={16} />
                  </button>
                </div>
              ))}
              {redirects.length === 0 && (
                <div className="py-6 text-center text-xs text-slate-400">No redirects configured yet</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* TAB 9: ENQUIRIES */}
      {activeTab === "enquiries" && (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-4">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">Website Enquiries</h2>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {enquiries.map((enq) => (
              <div key={enq.id} className="py-4 space-y-1">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm text-slate-900 dark:text-white">
                    {enq.name} ({enq.phone || enq.email})
                  </h4>
                  <span className="text-xs text-slate-400">{enq.created_at}</span>
                </div>
                <div className="text-xs font-semibold text-indigo-600 uppercase">
                  Type: {enq.type} {enq.subject ? `• ${enq.subject}` : ""}
                </div>
                <p className="text-xs text-slate-600 dark:text-slate-300">{enq.message}</p>
              </div>
            ))}
            {enquiries.length === 0 && (
              <div className="py-12 text-center text-slate-400">No enquiries received yet</div>
            )}
          </div>
        </div>
      )}

      {/* MODAL: Add Google Drive Links */}
      {driveModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 max-w-lg w-full rounded-2xl p-6 space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-slate-900 dark:text-white flex items-center gap-2">
                <IconBrandGoogleDrive size={20} className="text-blue-500" />
                Add Google Drive Media
              </h3>
              <button onClick={() => setDriveModalOpen(false)}>
                <IconX size={20} />
              </button>
            </div>

            <p className="text-xs text-slate-500">
              Paste public Google Drive share links (one per line). Videos, images, and 360° panoramas will be cached and served seamlessly.
            </p>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                Google Drive Share URLs
              </label>
              <textarea
                rows={5}
                value={driveLinksText}
                onChange={(e) => setDriveLinksText(e.target.value)}
                placeholder="https://drive.google.com/file/d/1A2B3C.../view?usp=sharing"
                className="w-full px-3 py-2 text-xs font-mono bg-slate-50 dark:bg-slate-900 border rounded-xl"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Media Type</label>
                <select
                  value={driveMediaType}
                  onChange={(e) => setDriveMediaType(e.target.value)}
                  className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border rounded-xl"
                >
                  <option value="image">Image</option>
                  <option value="video">Video</option>
                  <option value="panorama360">360° Panorama</option>
                  <option value="model3d">3D Model (GLB)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Tags (optional)</label>
                <input
                  type="text"
                  value={driveTags}
                  onChange={(e) => setDriveTags(e.target.value)}
                  placeholder="lobby, deluxe, restaurant"
                  className="w-full px-3 py-2 text-xs bg-slate-50 dark:bg-slate-900 border rounded-xl"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setDriveModalOpen(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDriveMedia}
                disabled={saving}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl text-xs"
              >
                {saving ? "Adding..." : "Import Media"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Edit Media SEO */}
      {editMediaModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={handleUpdateMedia}
            className="bg-white dark:bg-slate-800 max-w-md w-full rounded-2xl p-6 space-y-4 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-slate-900 dark:text-white">Edit Media SEO & Tags</h3>
              <button type="button" onClick={() => setEditMediaModal(null)}>
                <IconX size={20} />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Title</label>
              <input
                type="text"
                value={editMediaModal.title || ""}
                onChange={(e) => setEditMediaModal({ ...editMediaModal, title: e.target.value })}
                className="w-full px-3 py-2 text-xs border rounded-xl bg-slate-50 dark:bg-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">
                ALT Text (Critical for Google Image Search)
              </label>
              <input
                type="text"
                value={editMediaModal.alt || ""}
                onChange={(e) => setEditMediaModal({ ...editMediaModal, alt_text: e.target.value })}
                placeholder="Describe image accurately for visually impaired and search engines"
                className="w-full px-3 py-2 text-xs border rounded-xl bg-slate-50 dark:bg-slate-900"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">SEO File Slug</label>
              <input
                type="text"
                value={editMediaModal.seo_filename || ""}
                onChange={(e) => setEditMediaModal({ ...editMediaModal, seo_filename: e.target.value })}
                placeholder="luxury-room-with-lake-view"
                className="w-full px-3 py-2 text-xs border rounded-xl bg-slate-50 dark:bg-slate-900"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setEditMediaModal(null)}
                className="px-4 py-2 text-xs font-medium text-slate-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-xl text-xs"
              >
                Save SEO Details
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: New Redirect */}
      {newRedirectModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={handleSaveRedirect}
            className="bg-white dark:bg-slate-800 max-w-sm w-full rounded-2xl p-6 space-y-4 shadow-xl"
          >
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-base text-slate-900 dark:text-white">Add URL Redirect</h3>
              <button type="button" onClick={() => setNewRedirectModal(false)}>
                <IconX size={20} />
              </button>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">From Path</label>
              <input
                type="text"
                value={redirectForm.from_path}
                onChange={(e) => setRedirectForm({ ...redirectForm, from_path: e.target.value })}
                placeholder="/old-deluxe-room"
                className="w-full px-3 py-2 text-xs border rounded-xl bg-slate-50 dark:bg-slate-900 font-mono"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">To Path</label>
              <input
                type="text"
                value={redirectForm.to_path}
                onChange={(e) => setRedirectForm({ ...redirectForm, to_path: e.target.value })}
                placeholder="/rooms/deluxe-suite"
                className="w-full px-3 py-2 text-xs border rounded-xl bg-slate-50 dark:bg-slate-900 font-mono"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setNewRedirectModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-indigo-600 text-white font-medium rounded-xl text-xs"
              >
                Add Redirect
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
